import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../api";
import { DEFAULT_SETTINGS } from "../mock";
import { translations } from "../i18n/translations";
import { pageTranslations } from "../i18n/pages";
import { inquiryTranslations } from "../i18n/inquiries";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [corridors, setCorridors] = useState([]);
  const [services, setServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [ready, setReady] = useState(false);
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem("sl_lang");
    if (saved) return saved;
    const supported = ["en", "si", "ta", "it", "fil", "th", "ro", "bn", "vi"];
    const nav = (navigator.language || "en").toLowerCase();
    const base = nav.split("-")[0];
    const detected = base === "tl" ? "fil" : (supported.includes(base) ? base : "en");
    return detected;
  });
  const setLang = (code) => { setLangState(code); localStorage.setItem("sl_lang", code); };
  const t = useCallback((key) => {
    const dict = translations[lang] || translations.en;
    const pdict = pageTranslations[lang] || pageTranslations.en;
    return dict[key] ?? pdict[key] ?? inquiryTranslations[lang]?.[key] ?? translations.en[key] ?? pageTranslations.en[key] ?? inquiryTranslations.en[key] ?? key;
  }, [lang]);

  const loadContent = useCallback(async () => {
    try {
      const [s, c, sv, f, co, wh, of, ins, ct, rv, si] = await Promise.all([
        api.getSettings(), api.getCorridors(), api.getServices(),
        api.getFaqs(), api.getCountries(), api.getWarehouses(), api.getOffers(), api.getInsurance(), api.getContacts(), api.getReviews(), api.getSiteImages(),
      ]);
      setSettings(s); setCorridors(c); setServices(sv); setFaqs(f); setCountries(co); setWarehouses(wh); setOffers(of); setInsurance(ins); setContacts(ct); setReviews(rv); setSiteImages(si || {});
    } catch (e) { console.error("content load", e); }
  }, []);

  useEffect(() => {
    (async () => {
      if (localStorage.getItem("sl_token")) {
        try { setUser(await api.me()); } catch { localStorage.removeItem("sl_token"); }
      }
      await loadContent();
      setReady(true);
    })();
  }, [loadContent]);

  const login = async (email, password) => {
    const { token, user } = await api.login({ email, password });
    localStorage.setItem("sl_token", token); setUser(user); return user;
  };
  const signup = async (data) => {
    const { token, user } = await api.register(data);
    localStorage.setItem("sl_token", token); setUser(user); return user;
  };
  const logout = () => { localStorage.removeItem("sl_token"); setUser(null); };

  const saveSettings = async (s) => { const saved = await api.saveSettings(s); setSettings(saved); return saved; };
  const refreshReviews = async () => setReviews(await api.getReviews());
  const refreshSiteImages = async () => setSiteImages((await api.getSiteImages()) || {});

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") {
        api.getSiteImages().then((images) => setSiteImages(images || {})).catch(() => {});
        api.getSettings().then(setSettings).catch(() => {});
      }
    };
    window.addEventListener("focus", refresh);
    const timer = setInterval(refresh, 60000);
    return () => { clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);

  return (
    <AppContext.Provider value={{
      user, ready, login, signup, logout,
      settings, saveSettings,
      lang, setLang, t,
      corridors, services, faqs, countries, warehouses, offers, insurance, contacts, reviews, siteImages,
      loadContent, refreshReviews, refreshSiteImages,
      setCorridors, setServices, setFaqs, setCountries,
    }}>
      {children}
    </AppContext.Provider>
  );
}
